<template>
  <BasePageTemplate :title="resetPasswordTitleString" :description="resetPasswordDescriptionString" v-model:error="error">
    <DynamicForm v-model:data="newCredentialsData" :definition="formDefinition" :form-id="formId">
      <!-- New Password field -->
      <Field :label="newPasswordString" is-required target="password">
        <PasswordInput :ref="passwordInputId" v-model="newCredentialsData.password" :disabled="disabled" @enter="onPasswordEnterKey" />
      </Field>

      <PasswordPolicies
        :policies="globalConfigStore.passwordPolicies"
        v-model:data="newCredentialsData.password"
        v-model:valid="isPasswordValid"
      />

      <!-- Confirm Password field -->
      <Field :label="confirmNewPasswordString" is-required target="confirmPassword">
        <PasswordInput
          :ref="confirmPasswordInputId"
          v-model="newCredentialsData.confirmPassword"
          :disabled="disabled"
          @enter="onResetPasswordClick"
        />
      </Field>

      <MainButton :label="resetPasswordString" :disabled="disabled" :loading="disabled" @click="onResetPasswordClick" />
    </DynamicForm>

    <!-- Other buttons -->
    <div class="flex items-center justify-center">
      <Button
        icon="chevron_left"
        rounded
        outlined
        severity="secondary"
        :disabled="disabled"
        :aria-label="backString"
        v-tooltip.top="backString"
        @click="onBackClick"
      ></Button>
    </div>
  </BasePageTemplate>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, useId, useTemplateRef } from "vue";
import { useTranslation } from "i18next-vue";
import * as yup from "yup";
import { IFormDefinition } from "@common/core";
import { ProblemDetailsType } from "@common/types";
import { Button, DynamicForm, Field, MainButton } from "@common/ui";
import { PasswordPolicies } from "@common/ui/atoms/Form/PasswordPolicies";
import { BasePageTemplate, PasswordInput } from "@authentication/ui";
import { ResetCredentialsType } from "@authentication/types";
import { useGlobalConfigStore } from "@shared/stores";
import { useForm } from "@common/hooks";

// Emits
const emit = defineEmits<{
  (e: "next"): void;
  (e: "back"): void;
}>();

// Constants
const formId = useId();
const passwordInputId = useId();
const confirmPasswordInputId = useId();

// Variables
const { t } = useTranslation();
const globalConfigStore = useGlobalConfigStore();
const dataFrom = useForm([formId]);
const passwordInput = useTemplateRef<InstanceType<typeof PasswordInput>>(passwordInputId);
const confirmPasswordInput = useTemplateRef<InstanceType<typeof PasswordInput>>(confirmPasswordInputId);
const disabled = ref(false);
const error = ref<ProblemDetailsType | null>();
const isPasswordValid = ref(false);
const newCredentialsData = ref<ResetCredentialsType>({} as ResetCredentialsType);

// Strings
const backString = t("commons.back");
const confirmNewPasswordString = t("reset_credentials.confirm_new_password");
const confirmNewPasswordErrorString = t("reset_credentials.confirm_new_password_error");
const newPasswordString = t("reset_credentials.new_password");
const newPasswordErrorString = t("reset_credentials.new_password_error", { fieldName: newPasswordString });
const resetPasswordString = t("reset_credentials.reset_password");
const resetPasswordDescriptionString = t("reset_credentials.reset_password_description");
const resetPasswordTitleString = t("reset_credentials.reset_password_title");

// Forms
const formDefinition = {
  password: {
    spec: yup
      .string()
      .required()
      .min(globalConfigStore.passwordPolicies.minNumbers)
      .max(globalConfigStore.passwordPolicies.maxLength)
      .test("password-policies", newPasswordErrorString, () => isPasswordValid.value)
      .label(newPasswordString),
  },
  confirmPassword: {
    spec: yup
      .string()
      .required()
      .test(
        "confirm-password",
        confirmNewPasswordErrorString,
        () => newCredentialsData.value.password === newCredentialsData.value.confirmPassword
      )
      .label(confirmNewPasswordString),
  },
} as IFormDefinition;

// Methods
const onBackClick = () => {
  emit("back");
};

const onPasswordEnterKey = () => {
  confirmPasswordInput.value!.focus();
};

const onResetPasswordClick = async () => {
  disabled.value = true;
  error.value = null;

  dataFrom.cleanErrors();

  if (!(await dataFrom.isValidAsync())) {
    disabled.value = false;
    return;
  }

  emit("next");
};

// Hooks
onMounted(async () => {
  await nextTick(() => {
    passwordInput.value!.focus();
  });
});
</script>
